import React, { useState, useEffect } from "react";
import { 
  Card, 
  Button, 
  Alert, 
  Table, 
  Spinner, 
  Badge,
  Form,
  Row,
  Col,
  ButtonGroup
} from "react-bootstrap";
import { 
  CheckCircle, 
  XCircle,
  Globe,
  Eye,
  ArrowClockwise,
  Plus,
  Pencil,
  InfoCircle
} from 'react-bootstrap-icons';
import { toast } from "react-toastify";

import { 
  getCountries,
  getCountryScreenersByCountryId,
  upsertCountryScreener
} from "../services/APIClient";

import AddCountryScreenerModal from "./AddCountryScreenerModal";
import EditCountryScreenerWindowModal from "./EditCountryScreenerWindowModal";

const CountryScreenerSearch = ({ 
  availableScreeningPoints = [], 
  currentUser = "admin",
  getAvailableScreeningPoints
}) => {
  // Data states
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryScreeners, setCountryScreeners] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);

  // Available-for-country (filtered or fallback)
  const [countrySpecificScreeningPoints, setCountrySpecificScreeningPoints] = useState([]);
  const [usedFallbackAll, setUsedFallbackAll] = useState(false);
  const [matchedCount, setMatchedCount] = useState(0);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingScreeningPoints, setLoadingScreeningPoints] = useState(false);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [showEditWindowModal, setShowEditWindowModal] = useState(false);
  const [editingScreener, setEditingScreener] = useState(null);

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    setLoadingCountries(true);
    setError(null);
    try {
      const countriesData = await getCountries();
      setCountries(countriesData);
    } catch (err) {
      console.error("Error loading countries:", err);
      setError("Failed to load countries. Please try again.");
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleCountrySelect = async (countryId) => {
    if (!countryId) {
      setSelectedCountry(null);
      setCountryScreeners([]);
      setCountrySpecificScreeningPoints([]);
      setUsedFallbackAll(false);
      setMatchedCount(0);
      return;
    }

    const country = countries.find(c => c.id === countryId);
    if (!country) return;

    setLoading(true);
    setError(null);

    try {
      setSelectedCountry(country);
      await loadCountrySpecificScreeningPoints(countryId);
      await loadCountryScreeners(countryId);

      setSearchHistory(prev => {
        const filtered = prev.filter(c => c.id !== country.id);
        return [country, ...filtered].slice(0, 5);
      });

      toast.success(`Country screener data loaded for ${country.name}!`);
    } catch (err) {
      console.error("Error loading country data:", err);
      setError("Error loading country screener data. Please try again.");
      setSelectedCountry(null);
      setCountryScreeners([]);
      setCountrySpecificScreeningPoints([]);
      setUsedFallbackAll(false);
      setMatchedCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Normalize and match strictly by country id.
  const resolveScreenersForCountry = (list, countryId) => {
    const normalized = (list || []).map(a => ({
      ...a,
      isCountrySpecific:
        String(a.countryid ?? a.countryId ?? "") === String(countryId) ||
        a.isCountrySpecific === true
    }));

    const matched = normalized.filter(
      a => String(a.countryid ?? a.countryId ?? "") === String(countryId)
    );

    if (matched.length > 0) {
      return { finalList: matched, usedFallback: false, matchedCount: matched.length };
    }
    return { finalList: [], usedFallback: false, matchedCount: 0 };
  };

  const loadCountrySpecificScreeningPoints = async (countryId) => {
    if (!getAvailableScreeningPoints) {
      const { finalList, usedFallback, matchedCount } =
        resolveScreenersForCountry(availableScreeningPoints, countryId);
      setCountrySpecificScreeningPoints(finalList);
      setUsedFallbackAll(usedFallback);
      setMatchedCount(matchedCount);
      return;
    }

    setLoadingScreeningPoints(true);
    try {
      const fetched = await getAvailableScreeningPoints(countryId);
      const { finalList, usedFallback, matchedCount } =
        resolveScreenersForCountry(fetched, countryId);

      setCountrySpecificScreeningPoints(finalList);
      setUsedFallbackAll(usedFallback);
      setMatchedCount(matchedCount);

      if (!usedFallback && matchedCount > 0) {
        toast.info(`Found ${matchedCount} assessment(s) tied to this country.`);
      } else {
        toast.info(`No country-bound assessments found.`);
      }
    } catch (err) {
      console.error("Error loading country-specific screening points:", err);
      const { finalList, usedFallback, matchedCount } =
        resolveScreenersForCountry(availableScreeningPoints, countryId);
      setCountrySpecificScreeningPoints(finalList);
      setUsedFallbackAll(usedFallback);
      setMatchedCount(matchedCount);
    } finally {
      setLoadingScreeningPoints(false);
    }
  };

  const loadCountryScreeners = async (countryId) => {
    try {
      const screeners = await getCountryScreenersByCountryId(countryId);
      setCountryScreeners(screeners);
    } catch (err) {
      console.error("Error loading country screeners:", err);
      setCountryScreeners([]);
    }
  };

  const handleHistoryItemClick = async (country) => {
    setSelectedCountry(country);
    await loadCountrySpecificScreeningPoints(country.id);
    await loadCountryScreeners(country.id);
  };

  const handleRefreshScreeners = async () => {
    if (selectedCountry) {
      await loadCountrySpecificScreeningPoints(selectedCountry.id);
      await loadCountryScreeners(selectedCountry.id);
    }
  };

  const handleToggleScreener = async (screener) => {
    if (!selectedCountry) return;

    if (!screener.active) {
      handleEditCountry(selectedCountry);
      return;
    }

    try {
      setLoading(true);

      const screenerData = {
        active: false,
        created_by: screener.created_by || currentUser,
        updated_by: currentUser,
        dateopened: screener.dateopened,
        dateclosed: screener.dateclosed,
      };

      await upsertCountryScreener(selectedCountry.id, screener.assessmentid, screenerData);
      await loadCountryScreeners(selectedCountry.id);

      toast.success('Country screener disabled successfully!');
    } catch (err) {
      console.error("Error disabling country screener:", err);
      setError(`Failed to disable country screener: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewScreener = (screener) => {
    const screenerInfo = getScreenerInfo(screener.assessmentid);
    toast.info(
      <div>
        <strong>{screenerInfo?.name || 'Unknown Assessment'}</strong><br/>
        <small>
          Country: {selectedCountry?.name || 'Unknown'}<br/>
          Status: {getEffectiveWindowStatus(screener)}<br/>
          {screener.dateopened && `Opened: ${new Date(screener.dateopened).toLocaleDateString()}`}<br/>
          {screener.dateclosed && `Closed: ${new Date(screener.dateclosed).toLocaleDateString()}`}<br/>
          Last Updated: {screener.lastupdate ? new Date(screener.lastupdate).toLocaleDateString() : 'N/A'}
        </small>
      </div>,
      { autoClose: 5000 }
    );
  };

  const getScreenerInfo = (assessmentId) => {
  return countrySpecificScreeningPoints.find(point => 
    String(point.id) === String(assessmentId)
  );
};

  const handleCountryUpdated = async (country, selectedScreeners) => {
    setShowAddModal(false);
    setEditingCountry(null);
    if (selectedCountry && selectedCountry.id === country.id) {
      await loadCountryScreeners(country.id);
    }
    toast.success(`Successfully updated screeners for ${country.name}`);
  };

  const handleEditCountry = (country) => {
    setEditingCountry(country);
    setShowAddModal(true);
  };

  const handleEditScreenerWindow = (screener) => {
    setEditingScreener(screener);
    setShowEditWindowModal(true);
  };

  const handleScreenerWindowSaved = async () => {
    setShowEditWindowModal(false);
    setEditingScreener(null);
    if (selectedCountry) {
      await loadCountryScreeners(selectedCountry.id);
    }
  };

  const handleAddButtonClick = () => {
    setShowAddModal(true);
  };

  const getStatusBadge = (screener) => {
    const status = getEffectiveWindowStatus(screener);
    const variants = {
      open: "success",
      scheduled: "info",
      closed: "warning",
      disabled: "secondary",
    };
    return (
      <>
        <Badge bg={variants[status]}>
          {status === "open" ? <CheckCircle className="me-1" /> : <XCircle className="me-1" />}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
        {screener.active && screener.windowConfigured === false && (
          <small className="text-warning d-block mt-1">Needs dates</small>
        )}
      </>
    );
  };

  const modalAvailableList = selectedCountry
    ? countrySpecificScreeningPoints
    : availableScreeningPoints;

  return (
    <div>
      {/* Country Selection Section */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              <Globe className="me-2" />
              Country-Level Screener Control
            </span>
            <div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleAddButtonClick}
                className="me-2"
              >
                <Plus className="me-1" />
                Add Country Screeners
              </Button>
              <small className="text-muted">Select a country to manage screeners</small>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={8}>
              <Form.Group className="mb-3 mb-md-0" controlId="country-screener-country">
                <Form.Label className="fw-semibold">Select Country</Form.Label>
                <Form.Select
                  value={selectedCountry?.id || ""}
                  onChange={(e) => handleCountrySelect(e.target.value)}
                  disabled={loadingCountries || loading}
                >
                  <option value="">Choose a country...</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select a country to view and manage its screening assessments
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button 
                variant="outline-secondary" 
                onClick={loadCountries}
                disabled={loadingCountries}
                className="w-100"
              >
                {loadingCountries ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ArrowClockwise className="me-1" />
                    Refresh Countries
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Search History */}
          {searchHistory.length > 0 && (
            <div className="mt-3">
              <small className="text-muted mb-2 d-block">Recent Selections:</small>
              <div className="d-flex flex-wrap gap-2">
                {searchHistory.map(country => (
                  <Button
                    key={country.id}
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleHistoryItemClick(country)}
                    className="text-truncate"
                    style={{ maxWidth: '200px' }}
                  >
                    {country.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Country-Specific Screening Points Info */}
          {selectedCountry && countrySpecificScreeningPoints.length > 0 && (
            <Alert variant={usedFallbackAll ? "secondary" : "info"} className="mt-3 mb-0">
              <InfoCircle className="me-2" />
              <strong>Available Screeners for {selectedCountry.name}:</strong> {countrySpecificScreeningPoints.length} loaded.
              {!usedFallbackAll ? (
                <span className="ms-2">
                  <Badge bg="info">{matchedCount} Country-Matched</Badge>
                </span>
              ) : (
                <span className="ms-2">
                  <Badge bg="secondary">No Country Match — Showing All</Badge>
                </span>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Country Details & Screeners */}
      {selectedCountry && (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Globe className="me-2 text-primary" />
                <div>
                  <span className="fw-semibold">Country: {selectedCountry.name}</span>
                  <small className="text-muted ms-2 d-block">Country ID: {selectedCountry.id}</small>
                </div>
              </div>
              <div>
                <Badge bg="info" className="me-2">
                  {countryScreeners.length} Country Screener{countryScreeners.length !== 1 ? 's' : ''} Configured
                </Badge>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={() => handleEditCountry(selectedCountry)}
                  className="me-2"
                  disabled={loadingScreeningPoints}
                >
                  <Pencil className="me-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={handleRefreshScreeners}
                  disabled={loading || loadingScreeningPoints}
                >
                  {loadingScreeningPoints ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <ArrowClockwise className="me-1" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {(loading || loadingScreeningPoints) && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">
                    {loadingScreeningPoints ? "Loading available screeners..." : "Loading country screeners..."}
                  </span>
                </Spinner>
              </div>
            )}
            
            {!loading && !loadingScreeningPoints && countryScreeners.length === 0 ? (
              <div className="text-center py-4">
                <Globe className="text-muted mb-2" size={32} />
                <h6 className="text-muted mb-2">No Country Screeners Configured</h6>
                <p className="text-muted mb-3">
                  This country doesn't have any screening assessments configured yet.
                </p>
                <Alert variant={usedFallbackAll ? "secondary" : "info"} className="mt-3">
                  <strong>Available Screeners:</strong> {countrySpecificScreeningPoints.length} screening point(s) are available for configuration.
                  {!usedFallbackAll ? (
                    <>
                      <br />
                      <Badge bg="info" className="mt-1">
                        {matchedCount} Country-Matched
                      </Badge>
                    </>
                  ) : (
                    <>
                      <br />
                      <Badge bg="secondary" className="mt-1">
                        No Country Match — Showing All
                      </Badge>
                    </>
                  )}
                </Alert>
              </div>
            ) : !loading && !loadingScreeningPoints && (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Screening Assessment</th>
                    <th>Grade Level</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date Opened</th>
                    <th>Date Closed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {countryScreeners.map(screener => {
                    const screenerInfo = getScreenerInfo(screener.assessmentid);
                    
                    return (
                      <tr key={screener.id || screener.assessmentid}>
                        <td>
                          <div>
                            <strong>{screenerInfo?.name || 'Unknown Assessment'}</strong>
                            {screenerInfo?.description && (
                              <>
                                <br />
                                <small className="text-muted">{screenerInfo.description}</small>
                              </>
                            )}
                            {screenerInfo?.isCountrySpecific && (
                              <>
                                <br />
                                <Badge bg="info" className="mt-1">Country-Specific</Badge>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg={
                              screenerInfo.isGrade1 && screenerInfo.isGrade2 ? "warning" :
                              screenerInfo.isGrade2 ? "info" : 
                              screenerInfo.isGrade1 ? "primary" : "secondary"
                            }>
                              {screenerInfo.gradeLevel}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg="secondary">{screenerInfo.category}</Badge>
                          )}
                        </td>
                        <td>
                          {screenerInfo && (
                            <Badge bg={screenerInfo.isCustom ? "warning" : "success"}>
                              {screenerInfo.isCustom ? "Custom" : "Standard"}
                            </Badge>
                          )}
                        </td>
                        <td>{getStatusBadge(screener)}</td>
                        <td>
                          {screener.dateopened ? (
                            <small className="text-muted">
                              {new Date(screener.dateopened).toLocaleDateString()}
                            </small>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {screener.dateclosed ? (
                            <small className="text-muted">
                              {new Date(screener.dateclosed).toLocaleDateString()}
                            </small>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <ButtonGroup size="sm">
                            <Button
                              variant="outline-primary"
                              onClick={() => handleEditScreenerWindow(screener)}
                              disabled={loading}
                              title="Edit opening/closing dates"
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant={screener.active ? "outline-danger" : "outline-primary"}
                              onClick={() => handleToggleScreener(screener)}
                              disabled={loading}
                              title={screener.active ? "Disable country screener" : "Schedule country screener"}
                            >
                              {screener.active ? <XCircle /> : <CheckCircle />}
                            </Button>
                            <Button
                              variant="outline-info"
                              onClick={() => handleViewScreener(screener)}
                              title="View screener details"
                            >
                              <Eye />
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {/* Summary Section */}
            {!loading && !loadingScreeningPoints && countryScreeners.length > 0 && (
              <div className="mt-3 p-3 bg-light rounded">
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-success">
                        {countryScreeners.filter(s => getEffectiveWindowStatus(s) === "open").length}
                      </div>
                      <small className="text-muted">Open</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-secondary">
                        {countryScreeners.filter(s => getEffectiveWindowStatus(s) === "scheduled").length}
                      </div>
                      <small className="text-muted">Scheduled</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-primary">
                        {countryScreeners.filter(s => getEffectiveWindowStatus(s) === "closed").length}
                      </div>
                      <small className="text-muted">Closed</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h5 mb-0 text-info">
                        {countryScreeners.filter(s => getEffectiveWindowStatus(s) === "disabled").length}
                      </div>
                      <small className="text-muted">Disabled</small>
                    </div>
                  </Col>
                </Row>
                
                <Alert variant={usedFallbackAll ? "secondary" : "warning"} className="mt-3 mb-0">
                  <p className="mb-2">
                    <strong>Country-Level Control:</strong> These settings apply to all schools in {selectedCountry.name}. Changes here affect screener availability across the entire country.
                  </p>
                  {!usedFallbackAll ? (
                    <p className="mb-0">
                      <strong>Country-Specific Screeners:</strong> Showing only assessments bound to this country.
                    </p>
                  ) : (
                    <p className="mb-0">
                      <strong>No direct country matches:</strong> Showing all available screeners so you can still configure access.
                    </p>
                  )}
                </Alert>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* MODAL COMPONENT */}
      <AddCountryScreenerModal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setEditingCountry(null);
        }}
        availableScreeningPoints={modalAvailableList}
        onCountryUpdated={handleCountryUpdated}
        currentUser={currentUser}
        editMode={!!editingCountry}
        initialCountry={editingCountry || selectedCountry || null}
      />

      <EditCountryScreenerWindowModal
        show={showEditWindowModal}
        onHide={() => {
          setShowEditWindowModal(false);
          setEditingScreener(null);
        }}
        country={selectedCountry}
        screener={editingScreener}
        screenerName={editingScreener ? getScreenerInfo(editingScreener.assessmentid)?.name : null}
        currentUser={currentUser}
        onSaved={handleScreenerWindowSaved}
      />
    </div>
  );
};

const getEffectiveWindowStatus = (screener) => {
  if (screener?.windowStatus) return screener.windowStatus;
  if (!screener?.active) return "disabled";
  const now = new Date();
  if (screener.dateopened && now < new Date(screener.dateopened)) return "scheduled";
  if (screener.dateclosed && now > new Date(screener.dateclosed)) return "closed";
  return "open";
};

export default CountryScreenerSearch;
